import PropTypes from 'prop-types';
import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import {
  Play,
  Square,
  TrashOpen,
} from '../../../assets/js/svg/gui';
import { useTownAssets } from '../../../stores';
import {
  logError,
} from '../../../utils';
import secondsToTime from '../../../utils/dateTime/secondsToTime';
import { Button } from '../../buttons';
import {
  Row,
} from '../../display';

import ChatAudioRecorderTicks from './ChatAudioRecorderTicks';

const audioTicks = 20; // Number of samples to generate
const samplesPerTick = 20; // Each tick represents 30 samples for slower movement
const micPopDurationS = 0.4;
const micGain = 5;

// Detect supported audio format
// Prefer webm;codecs=opus for cross-browser compatibility:
// - Chrome/Firefox: play webm natively
// - Safari: play webm via ogv.js (WASM decoder)
// Fallback to mp4+AAC for Safari recording (Safari can't record webm)
// AAC (mp4a.40.2) is universally decodable, unlike Opus-in-mp4
const getSupportedMimeType = () => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2', // AAC-LC codec - universally decodable
  ];
  return types.find((type) => MediaRecorder.isTypeSupported(type)) || '';
};

// Recording state machine states
const RECORDING_STATES = {
  IDLE: 'idle',
  INITIALIZING: 'initializing',
  RECORDING: 'recording',
  STOPPING: 'stopping',
  ERROR: 'error',
};

function ChatAudioRecorder(props) {
  const {
    recordingAudio,
    audioContextRef,
    audioAnalyzerRef,
    audioBlob,
    setAudioBlob,
    audioDuration,
    setAudioDuration,
    CustomButton,
    composeBackgroundColor,
    cancelAudio,
  } = props;
  // State machine ref to track recording state
  const recordingState = useRef({
    state: RECORDING_STATES.IDLE,
    recorder: null,
    stream: null,
    rawSource: null,
    startTime: null,
    chunks: [],
  });
  const { assets } = useTownAssets();
  const { translate } = assets;

  // UI state
  const [audioBuffer, setAudioBuffer] = useState();
  // AudioBufferSourceNode set during playing:
  const [playbackSourceNode, setPlaybackSourceNode] = useState(false);
  const [displayAudioSource, setDisplayAudioSource] = useState();
  const [playTime, setPlayTime] = useState();
  // recError: false = no error, 'permission' = mic permission denied, 'generic' = other error
  const [recError, setRecError] = useState(false);
  const [audioLevels, setAudioLevels] = useState([]);

  // Animation refs
  const enableAnimateAudioRef = useRef(false);
  const lastAnimateAudioRef = useRef(false);
  const audioLevelsRef = useRef([]);
  const sampleCountRef = useRef(0);
  const currentTickSamplesRef = useRef([]);

  // Cleanup function that ensures proper order
  const cleanupRecording = useCallback(
    () => {
      const state = recordingState.current;

      // 1. Disconnect from analyzer first
      if (state.gainNode && audioAnalyzerRef.current) {
        try {
          state.gainNode.disconnect(audioAnalyzerRef.current);
        } catch (e) {
          logError(
            `cleanupRecording: Analyzer disconnect failed:${e.message}`,
            'ChatAudioRecorder.cleanupRecording.disconnectFailed',
            { err: e?.message },
          );
        }
      }

      // 2. Clear display audio source
      setDisplayAudioSource(null);

      // 3. Disconnect all Web Audio API nodes in reverse order
      if (state.gainNode && state.outputDestination) {
        try {
          state.gainNode.disconnect(state.outputDestination);
        } catch (e) {
          logError(`cleanupRecording: Gain to destination disconnect failed:${e.message}`);
        }
      }

      if (state.inputSource && state.gainNode) {
        try {
          state.inputSource.disconnect(state.gainNode);
        } catch (e) {
          logError(`cleanupRecording: Input to gain disconnect failed:${e.message}`);
        }
      }

      // 4. Disconnect all nodes completely
      if (state.gainNode) {
        try {
          state.gainNode.disconnect();
        } catch (e) {
          logError(`cleanupRecording: Gain node complete disconnect failed:${e.message}`);
        }
      }

      if (state.inputSource) {
        try {
          state.inputSource.disconnect();
        } catch (e) {
          logError(`cleanupRecording: Input source complete disconnect failed:${e.message}`);
        }
      }

      if (state.outputDestination) {
        try {
          state.outputDestination.disconnect();
        } catch (e) {
          logError(`cleanupRecording: Output destination complete disconnect failed:${e.message}`);
        }
      }

      // 5. Stop all MediaStream tracks from BOTH streams
      if (state.inputStream) {
        state.inputStream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      if (state.processedStream) {
        state.processedStream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      // 6. Reset the state machine
      recordingState.current = {
        state: RECORDING_STATES.IDLE,
        recorder: null,
        inputStream: null,
        processedStream: null,
        inputSource: null,
        gainNode: null,
        outputDestination: null,
        startTime: null,
        chunks: [],
      };
    },
    [audioAnalyzerRef],
  );

  // Start recording process
  const startRecording = useCallback(
    async () => {
      if (recordingState.current.state !== RECORDING_STATES.IDLE) {
        logError(
          `startRecording: Cannot start, current state is ${recordingState.current.state}`,
          'ChatAudioRecorder.startRecording.invalidState',
          { currentState: recordingState.current.state },
        );
        return;
      }

      recordingState.current.state = RECORDING_STATES.INITIALIZING;
      recordingState.current.chunks = [];

      // Reset UI state
      setAudioBuffer();
      setAudioBlob();
      setAudioLevels([]);
      audioLevelsRef.current = [];
      sampleCountRef.current = 0;
      currentTickSamplesRef.current = [];
      setRecError(false);

      try {
      // Step 1: Get raw microphone stream
        const inputStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
            sampleSize: 16,
            channelCount: 1,
            volume: 1.0,
          },
        });

        // Check if we should still continue
        if (recordingState.current.state !== RECORDING_STATES.INITIALIZING) {
          inputStream.getTracks().forEach((track) => track.stop());
          return;
        }

        // Step 2: Create Web Audio API processing chain
        const audioContext = audioContextRef.current;

        // Create nodes for audio processing
        const inputSource = audioContext.createMediaStreamSource(inputStream);
        const gainNode = audioContext.createGain();
        const outputDestination = audioContext.createMediaStreamDestination();

        // Set gain for volume boost (adjust as needed)
        gainNode.gain.value = micGain;

        // Connect the audio processing chain: input -> gain -> output
        inputSource.connect(gainNode);
        gainNode.connect(outputDestination);

        // Also connect to analyzer for visual feedback (from the boosted signal)
        if (audioAnalyzerRef.current) {
          gainNode.connect(audioAnalyzerRef.current);
          setDisplayAudioSource(gainNode);
        }

        // Step 3: Get the processed stream (this is the key!)
        const processedStream = outputDestination.stream;

        // Step 4: Let the audio system stabilize BEFORE creating MediaRecorder
        // console.log('Waiting for audio system to stabilize...');
        await new Promise((resolve) => {
          setTimeout(resolve, micPopDurationS * 1000);
        });

        // Check again if we should still continue after stabilization delay
        if (recordingState.current.state !== RECORDING_STATES.INITIALIZING) {
          inputStream.getTracks().forEach((track) => track.stop());
          inputSource.disconnect();
          gainNode.disconnect();
          outputDestination.disconnect();
          return;
        }

        // Step 5: NOW create MediaRecorder from the STABILIZED processed stream
        const mimeType = getSupportedMimeType();
        const mediaRecorder = new MediaRecorder(
          processedStream,
          {
            mimeType,
            audioBitsPerSecond: 32000,
          },
        );

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordingState.current.chunks.push(event.data);
          }
        };

        // Step 6: Start recording the stabilized processed stream
        mediaRecorder.start(250); // Every 250ms chunks
        const startOffset = audioDuration || 0;
        const startTime = audioContext.currentTime - startOffset;

        // Step 7: Store all resources for proper cleanup
        recordingState.current = {
          state: RECORDING_STATES.RECORDING,
          recorder: mediaRecorder,
          inputStream, // Original microphone stream
          processedStream, // Processed stream being recorded
          inputSource, // Web Audio source node
          gainNode, // Gain node for volume control
          outputDestination, // Destination node
          startTime,
          chunks: [],
          mimeType,
        };
      } catch (e) {
        logError(
          `startRecording error: ${e?.message}`,
          'ChatAudioRecorder.startRecording.error',
          {
            err: e?.message,
            ...(e?.name && { name: e.name }),
          },
        );
        // Reset to known state before cleanup
        recordingState.current.state = RECORDING_STATES.ERROR;
        // Detect permission denial (NotAllowedError) vs other errors
        const isPermissionError = (e.name === 'NotAllowedError') || (e.name === 'PermissionDeniedError');
        setRecError(isPermissionError ? 'permission' : 'generic');
        cleanupRecording();
        // Reset parent state so user can retry by clicking record again
        if (cancelAudio) cancelAudio();
      }
    },
    [
      audioContextRef, audioAnalyzerRef, audioDuration, setAudioBlob,
      cleanupRecording, cancelAudio,
    ],
  );

  // Stop recording process
  const stopRecording = useCallback(
    async () => {
      const state = recordingState.current;

      // Only stop if we're actually recording
      if (state.state !== RECORDING_STATES.RECORDING) {
      // Don't log error for IDLE state (expected after failed permission) or ERROR state
        if ((state.state !== RECORDING_STATES.IDLE) && (state.state !== RECORDING_STATES.ERROR)) {
          logError(
            `stopRecording: Cannot stop, current state is ${state.state}`,
            'ChatAudioRecorder.stopRecording.invalidState',
            { currentState: state.state },
          );
        }
        return;
      }

      recordingState.current.state = RECORDING_STATES.STOPPING;

      // Create a promise to wait for the MediaRecorder to finish
      const stopPromise = new Promise((resolve) => {
        if (state.recorder && (state.recorder.state === 'recording')) {
          // Same array, not a copy: requestData() and stop() still push their last chunks into it,
          // and cleanup replaces the array rather than emptying it
          const chunksToProcess = recordingState.current.chunks;
          const { mimeType } = recordingState.current;

          state.recorder.onstop = () => {
            if (chunksToProcess.length > 0) {
              const newAudioBlob = new Blob(chunksToProcess, { type: mimeType });
              // Call setAudioBlob with the blob as parameter
              setAudioBlob(newAudioBlob);
            } else {
              console.error('No chunks to create blob from!');
            }
            resolve();
          };

          // Request data before stopping to ensure we get all chunks
          state.recorder.requestData();
          // Small delay to ensure data is flushed
          setTimeout(
            () => {
              state.recorder.stop();
            },
            100,
          );
        } else {
          resolve();
        }
      });

      // Wait for MediaRecorder to finish
      await stopPromise;

      // Now cleanup
      cleanupRecording();
    },
    [cleanupRecording, setAudioBlob],
  );

  // Handle recordingAudio prop changes
  useEffect(
    () => {
      if (recordingAudio && audioContextRef.current) {
        // Use void to make it clear we do not want to wait this async function:
        void startRecording();
      } else if (!recordingAudio) {
      // If we're initializing, wait for it to complete before stopping
        if (recordingState.current.state === RECORDING_STATES.INITIALIZING) {
          const checkInterval = setInterval(
            () => {
              if (recordingState.current.state !== RECORDING_STATES.INITIALIZING) {
                clearInterval(checkInterval);
                if (recordingState.current.state === RECORDING_STATES.RECORDING) {
                  // Use void to make it clear we do not want to wait this async function:
                  void stopRecording();
                }
              }
            },
            50,
          );
          return () => clearInterval(checkInterval);
        }
        // Use void to make it clear we do not want to wait this async function:
        void stopRecording();
      }

      return undefined;
    },
    [recordingAudio, audioContextRef, startRecording, stopRecording],
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      cleanupRecording();

      // Stop any playing audio
      setPlaybackSourceNode((current) => {
        if (current) current.stop();
        return undefined;
      });
    },
    [cleanupRecording],
  );

  // Audio blob processing
  useEffect(
    () => {
      const generateAudioLevels = (buffer) => {
        const channelData = buffer.getChannelData(0);
        const samplesPerSegment = Math.floor(channelData.length / audioTicks);
        const levels = [];

        for (let i = 0; i < audioTicks; i += 1) {
          const start = i * samplesPerSegment;
          const end = Math.min(start + samplesPerSegment, channelData.length);

          let rms = 0;
          let count = 0;
          for (let j = start; j < end; j += 1) {
            rms += channelData[j] * channelData[j];
            count += 1;
          }

          rms = Math.sqrt(rms / count);
          let level = Math.min(100, Math.max(0, Math.log10(rms * 100 + 1) * 50));

          if (rms > 0.001) {
            level = Math.max(level, 15);
          }

          levels.push(Math.floor(level));
        }

        setAudioLevels(levels);
        audioLevelsRef.current = levels;
      };

      if (audioBlob && audioContextRef.current) {
        console.log('Processing audio blob with Web Audio API approach...');
        audioBlob
          .arrayBuffer()
          .then((arrayBuffer) => {
            console.log(`Got array buffer of size: ${arrayBuffer.byteLength}`);
            return audioContextRef.current.decodeAudioData(
              arrayBuffer,
              (decodedBuffer) => {
                console.log(`Decoded audio: duration=${decodedBuffer.duration}s, sampleRate=${decodedBuffer.sampleRate}`);

                // No trimming needed since mic pop should be prevented by stabilization delay
                setAudioBuffer(decodedBuffer);
                setAudioDuration(Math.floor(decodedBuffer.duration * 10) / 10);
                generateAudioLevels(decodedBuffer);
              },
              (error) => {
                console.error('Failed to decode audio data:', error);
              },
            );
          })
          .catch((error) => {
            console.error('Failed to get array buffer:', error);
          });
      }
    },
    [audioBlob, audioContextRef, setAudioDuration],
  );

  // Playback connection to analyzer
  useEffect(
    () => {
      if (playbackSourceNode
          && audioAnalyzerRef.current
          && recordingState.current.state === RECORDING_STATES.IDLE) {
        try {
          playbackSourceNode.connect(audioAnalyzerRef.current);
          setDisplayAudioSource(playbackSourceNode);
        } catch (e) {
          logError(
            `Failed to connect playback to analyzer:${e.message}`,
            'ChatAudioRecorder.playbackConnect.error',
            { err: e?.message },
          );
        }

        return () => {
          try {
          // eslint-disable-next-line react-hooks/exhaustive-deps
            playbackSourceNode.disconnect(audioAnalyzerRef.current);
          } catch (e) {
            logError(
              `Playback disconnect failed:${e.message}`,
              'ChatAudioRecorder.playbackDisconnect.error',
              { err: e?.message },
            );
          }
          setDisplayAudioSource(null);
        };
      }

      return undefined;
    },
    [playbackSourceNode, audioAnalyzerRef],
  );

  const handlePlayClick = useCallback(
    () => {
      if (audioBuffer) {
        if (!playbackSourceNode) {
          const newPlaybackSourceNode = audioContextRef.current.createBufferSource();
          newPlaybackSourceNode.connect(audioContextRef.current.destination);
          newPlaybackSourceNode.buffer = audioBuffer;
          setPlaybackSourceNode(newPlaybackSourceNode);

          const startOffset = 0;
          newPlaybackSourceNode.start(0, startOffset);

          recordingState.current.playbackStartTime = audioContextRef.current.currentTime
                                                - startOffset;

          newPlaybackSourceNode.addEventListener(
            'ended',
            () => {
              setPlaybackSourceNode();
              recordingState.current.playbackStartTime = null;
              setPlayTime();
            },
          );
        } else {
          playbackSourceNode.stop();
          setPlaybackSourceNode();
          recordingState.current.playbackStartTime = null;
          setPlayTime();
        }
      }
    },
    [audioContextRef, audioBuffer, playbackSourceNode],
  );

  const animateAudio = useCallback(
    () => {
      if (audioAnalyzerRef.current && enableAnimateAudioRef.current) {
        const bufferLength = audioAnalyzerRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        audioAnalyzerRef.current.getByteTimeDomainData(dataArray);

        let rms = 0;
        for (let i = 0; i < dataArray.length; i += 1) {
          const sample = (dataArray[i] - 128) / 128;
          rms += sample * sample;
        }
        rms = Math.sqrt(rms / dataArray.length);

        let currentLevel = Math.min(100, Math.max(0, Math.log10(rms * 100 + 1) * 50));
        if (rms > 0.01) {
          currentLevel = Math.max(currentLevel, 20);
        }

        if (recordingState.current.state === RECORDING_STATES.RECORDING) {
          currentTickSamplesRef.current.push(currentLevel);
          sampleCountRef.current += 1;

          if (sampleCountRef.current >= samplesPerTick) {
            const avgLevel = currentTickSamplesRef.current.reduce((sum, level) => sum + level, 0)
                          / currentTickSamplesRef.current.length;

            audioLevelsRef.current = [...audioLevelsRef.current, Math.floor(avgLevel)];
            setAudioLevels([...audioLevelsRef.current]);

            currentTickSamplesRef.current = [];
            sampleCountRef.current = 0;
          }
        }

        // Track time for both recording and playback
        let timeToDisplay = null;

        if (recordingState.current.state === RECORDING_STATES.RECORDING
          && recordingState.current.startTime) {
        // During recording
          timeToDisplay = audioContextRef.current.currentTime
                        - recordingState.current.startTime;
        } else if (playbackSourceNode && recordingState.current.playbackStartTime) {
        // During playback
          timeToDisplay = audioContextRef.current.currentTime
                        - recordingState.current.playbackStartTime;
        }

        if (timeToDisplay !== null) {
          setPlayTime(Math.floor(timeToDisplay * 10) / 10);
        } else {
          setPlayTime();
        }

        lastAnimateAudioRef.current = requestAnimationFrame(animateAudio);
      } else {
        setPlayTime();
      }
    },
    [audioContextRef, audioAnalyzerRef, playbackSourceNode],
  );

  useEffect(
    () => {
      if (lastAnimateAudioRef.current) {
        cancelAnimationFrame(lastAnimateAudioRef.current);
      }
      if (displayAudioSource) {
        enableAnimateAudioRef.current = true;
        animateAudio();
      } else {
        enableAnimateAudioRef.current = false;
      }
    },
    [animateAudio, displayAudioSource],
  );

  const clickTrash = useCallback(
    () => {
      if (playbackSourceNode) {
        playbackSourceNode.stop();
        setPlaybackSourceNode();
      }
      cancelAudio();
    },
    [cancelAudio, playbackSourceNode],
  );

  const isRecording = recordingState.current.state === RECORDING_STATES.RECORDING;

  // Calculate playback progress for visualization
  const playProgress = playTime && audioDuration ? playTime / audioDuration : 0;

  return (
    <Row className="p-1.5 flex-1 rounded-md border border-pxMainOutline h-16">
      {
        (CustomButton)
          ? (
              <CustomButton
                className="h-9 w-9 rounded-full p-1.5 shadow-button"
                disabled={!audioBuffer}
                onClick={handlePlayClick}
                Icon={(playbackSourceNode)
                  ? <Square className="w-full h-full fill-pxAccentBlue" />
                  : <Play className="w-full h-full fill-pxAccentBlue" />}
                outsideColor={composeBackgroundColor}
                outsideNeuMorphismColor={composeBackgroundColor}
              />
            )
          : (
              <Button
                disabled={!audioBuffer}
                onClick={handlePlayClick}
                imgSrc={playbackSourceNode
                  ? '/images/svg/gui/stop.svg'
                  : '/images/svg/gui/play.svg'}
                noFrame
              />
            )
      }

      { (!recError)
        ? (
            <>
              <ChatAudioRecorderTicks
                audioLevels={audioLevels}
                isRecording={isRecording}
                isPlaying={!!playbackSourceNode}
                playProgress={playProgress}
                totalTicks={audioTicks}
              />

              {audioDuration !== undefined
                ? (
                    <span>{secondsToTime(audioDuration)}</span>
                  )
                : null}
            </>
          )
        : (
            <div className="error text-pxUiAlert text-sm flex-1 text-center">
              {recError === 'permission'
                ? translate('permissionDenied')
                : translate('error')}
            </div>
          )}

      {
        (CustomButton)
          ? (
              <CustomButton
                className="h-9 w-9 rounded-full p-1.5 shadow-button"
                disabled={!audioBuffer}
                onClick={clickTrash}
                Icon={<TrashOpen className="w-full h-full fill-pxAccentBlue" />}
                outsideColor={composeBackgroundColor}
                outsideNeuMorphismColor={composeBackgroundColor}
              />
            )
          : (
              <Button
                disabled={!audioBuffer}
                onClick={clickTrash}
                imgSrc="/images/svg/gui/cross.svg"
                noFrame
              />
            )
      }
    </Row>
  );
}

ChatAudioRecorder.propTypes = {
  recordingAudio: PropTypes.bool.isRequired,
  audioContextRef: PropTypes.shape({
    current: PropTypes.shape({
      decodeAudioData: PropTypes.func,
      createGain: PropTypes.func,
      createMediaStreamDestination: PropTypes.func,
      createMediaStreamSource: PropTypes.func,
      createBufferSource: PropTypes.func,
      destination: PropTypes.shape({}),
      currentTime: PropTypes.number,
      createBuffer: PropTypes.func,
    }).isRequired,
  }).isRequired,
  audioAnalyzerRef: PropTypes.shape({
    current: PropTypes.shape({
      frequencyBinCount: PropTypes.number,
      getByteTimeDomainData: PropTypes.func,
    }).isRequired,
  }).isRequired,
  audioBlob: PropTypes.shape({
    arrayBuffer: PropTypes.func,
    size: PropTypes.number,
  }),
  setAudioBlob: PropTypes.func.isRequired,
  audioDuration: PropTypes.number,
  setAudioDuration: PropTypes.func.isRequired,
  CustomButton: PropTypes.node,
  composeBackgroundColor: PropTypes.string,
  cancelAudio: PropTypes.func,
};

export default ChatAudioRecorder;
